/**
 * New check form — scope toggle + criteria picker + Run.
 *
 * Split out of ReviewTab so the full-response reader can host the same form
 * without pulling in the whole panel.
 */

import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { CriteriaPicker, CriteriaPickerHint } from '@/components/drafting/CriteriaPicker'

// ===================
// Scope toggle
// ===================

function ScopeToggle({
  canToggle,
  value,
  sectionKey,
  sectionTitle,
  onChange,
}: {
  canToggle: boolean
  value: 'artifact' | 'section'
  sectionKey: string
  sectionTitle: string | null
  onChange: (next: 'artifact' | 'section') => void
}) {
  if (!canToggle) {
    return (
      <span className="text-[11px] text-muted-foreground">
        Reviewing: {sectionTitle ?? 'assigned section'}
      </span>
    )
  }
  return (
    <div className="inline-flex rounded-md border bg-muted p-0.5">
      <button
        type="button"
        onClick={() => onChange('artifact')}
        className={cn(
          'px-2 py-0.5 text-xs rounded transition-colors',
          value === 'artifact'
            ? 'bg-card shadow-sm font-medium text-foreground'
            : 'text-muted-foreground hover:text-foreground',
        )}
      >
        Full document
      </button>
      <button
        type="button"
        onClick={() => onChange('section')}
        disabled={!sectionKey}
        className={cn(
          'px-2 py-0.5 text-xs rounded transition-colors',
          value === 'section'
            ? 'bg-card shadow-sm font-medium text-foreground'
            : 'text-muted-foreground hover:text-foreground',
          !sectionKey && 'opacity-50 cursor-not-allowed',
        )}
      >
        This section
      </button>
    </div>
  )
}

// ===================
// New check form
// ===================

export function NewReviewForm({
  onCancel,
  onRun,
  canRun,
  outlineHasSections,
  onManageCriteria,
  selectedCount,
  isRunning,
  canToggleScope,
  scope,
  sectionKey,
  sectionTitle,
  scopeNotice,
  onScopeChange,
}: {
  onCancel: () => void
  onRun: () => void
  canRun: boolean
  outlineHasSections: boolean
  onManageCriteria: () => void
  selectedCount: number
  isRunning: boolean
  canToggleScope: boolean
  scope: 'artifact' | 'section'
  sectionKey: string
  sectionTitle: string | null
  /** Set when switching scope dropped criteria that no longer apply. */
  scopeNotice: string | null
  onScopeChange: (next: 'artifact' | 'section') => void
}) {
  return (
    <div className="space-y-4">
      <div>
        <h4 className="text-sm font-medium mb-0.5">New check</h4>
        <p className="text-meta">
          Pick what to check and the criteria to check it against.
        </p>
      </div>

      {/* Scope — first decision before criteria. Hidden pills when the
          caller can only review their assigned section (falls back to a
          read-only label). */}
      <div className="space-y-1.5">
        <label className="block text-xs font-medium text-foreground">Scope</label>
        {canToggleScope ? (
          <ScopeToggle
            canToggle
            value={scope}
            sectionKey={sectionKey}
            sectionTitle={sectionTitle}
            onChange={onScopeChange}
          />
        ) : (
          <p className="text-meta">
            Reviewing {sectionTitle ?? 'assigned section'}
          </p>
        )}
        {scopeNotice && (
          <p className="text-meta">{scopeNotice}</p>
        )}
      </div>

      <div className="space-y-1.5">
        <label className="block text-xs font-medium text-foreground">Criteria</label>
        <CriteriaPicker scope={scope} onManageClick={onManageCriteria} />
      </div>

      <div className="flex items-center gap-2 pt-2 border-t">
        <CriteriaPickerHint count={selectedCount} />
        <div className="ml-auto flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={onCancel} disabled={isRunning}>
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={onRun}
            disabled={!canRun}
            title={!outlineHasSections ? 'Nothing to check yet — add draft content first' : undefined}
          >
            {isRunning ? 'Starting…' : 'Run check'}
          </Button>
        </div>
      </div>
    </div>
  )
}
